import { isValid } from "date-fns";

import { type CalendarDate, createDate, isCalendarDate } from "../dateAdapter";

type BoundDate = string | Date | CalendarDate;

function toComparableTime(
  value: BoundDate,
  dateFormat?: string,
  localization?: string,
): number | undefined {
  if (value instanceof Date) {
    return isValid(value) ? value.getTime() : undefined;
  }
  if (isCalendarDate(value)) {
    const date = value.toDate();
    return isValid(date) ? date.getTime() : undefined;
  }
  if (typeof value === "string" && value) {
    const parsed = createDate(value, dateFormat, localization);
    if (parsed) {
      const date = parsed.toDate();
      return isValid(date) ? date.getTime() : undefined;
    }
  }
  return undefined;
}

/**
 * Widen min/max so the current value stays selectable.
 * The pickers crash when value is outside the allowed range.
 */
export function clampMinMaxToValue<T extends BoundDate>(
  value: T | undefined | null,
  minDate: T | undefined,
  maxDate: T | undefined,
  dateFormat?: string,
  localization?: string,
): { minDate: T | undefined; maxDate: T | undefined } {
  if (!value) {
    return { minDate, maxDate };
  }

  const valueTime = toComparableTime(value, dateFormat, localization);
  if (valueTime === undefined) {
    return { minDate, maxDate };
  }

  let nextMin = minDate;
  let nextMax = maxDate;

  if (minDate !== undefined) {
    const minTime = toComparableTime(minDate, dateFormat, localization);
    if (minTime !== undefined && valueTime < minTime) {
      nextMin = value;
    }
  }
  if (maxDate !== undefined) {
    const maxTime = toComparableTime(maxDate, dateFormat, localization);
    if (maxTime !== undefined && valueTime > maxTime) {
      nextMax = value;
    }
  }

  return { minDate: nextMin, maxDate: nextMax };
}

/**
 * Filter out all moments that don't have
 * all dates in month disabled.
 * @param {*} moments
 * @return An array of moments; each of these moments
 * doesn't have any selectable date in month.
 */
export function getDisabledMonths(
  moments: CalendarDate[],
): CalendarDate[] | undefined {
  if (!moments) {
    return;
  }
  const disabledMonths: CalendarDate[] = [];
  const checkedMonths: any[] = [];
  for (const m of moments) {
    if (checkedMonths.indexOf(m.month()) < 0) {
      const momentsForMonth = moments.filter(
        (mForMonth) => mForMonth.month() === m.month(),
      );
      const momentsForMonthUniq: any[] = [];
      for (const mForMonth of momentsForMonth) {
        if (momentsForMonthUniq.indexOf(mForMonth) < 0) {
          momentsForMonthUniq.push(mForMonth);
        }
      }
      if (momentsForMonthUniq.length === m.daysInMonth()) {
        disabledMonths.push(m);
      }
      checkedMonths.push(m);
    }
  }

  return disabledMonths;
}

/**
 * Filter out all moments that don't have
 * all months in year disabled.
 * @param {*} moments
 * @return An array of moments; each of these moments
 * doesn't have any selectable month in year.
 */
export function getDisabledYears(
  moments: CalendarDate[],
): CalendarDate[] | undefined {
  if (!moments) {
    return;
  }
  const disabledYears: CalendarDate[] = [];
  const checkedYears: any[] = [];
  for (const y of moments) {
    if (checkedYears.indexOf(y.year()) < 0) {
      const momentsForYear = getDisabledMonths(
        moments.filter((mForYear) => mForYear.year() === y.year()),
      );
      const momentsForYearUniq: any[] = [];
      for (const mForYear of momentsForYear!) {
        if (momentsForYearUniq.indexOf(mForYear) < 0) {
          momentsForYearUniq.push(mForYear);
        }
      }
      if (momentsForYearUniq.length === 12) {
        disabledYears.push(y);
      }
      checkedYears.push(y);
    }
  }

  return disabledYears;
}
